import React, { useMemo } from "react";
import { motion } from "framer-motion";
import PinnacleDiagram from "./PinnacleDiagram";
import PinnacleResultList from "./PinnacleResultList";

const formatDate = (value) => {
  if (!value) return "—";
  const formatter = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });

  try {
    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, yearStr, monthStr, dayStr] = match;
        const [year, month, day] = [yearStr, monthStr, dayStr].map(Number);
        const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        return formatter.format(date);
      }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatter.format(date);
  } catch {
    return value;
  }
};

const InfoBlock = ({ message, icon = "ℹ️", actionLabel, onAction }) => (
  <div className="bg-white/80 border border-[#d4af37]/60 rounded-2xl p-6 text-center shadow-sm">
    <div className="text-3xl mb-2">{icon}</div>
    <p className="text-sm text-gray-700 mb-3">{message}</p>
    {actionLabel && onAction && (
      <button
        onClick={onAction}
        className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-morado text-white hover:shadow-lg transition"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export default function PinnacleCard({ profileName, birthDate, data, loading, error, reason, onRetry }) {
  if (loading) {
    return <InfoBlock message="Calculando tu Pináculo..." icon="⏳" />;
  }

  if (reason) {
    return <InfoBlock message={reason} icon="💡" />;
  }

  if (error) {
    return <InfoBlock message={error} icon="⚠️" actionLabel="Reintentar" onAction={onRetry} />;
  }

  if (!data) {
    return null;
  }

  const nodes = data.nodes || {};

  const diagramData = useMemo(() => {
    const ids = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const preferred = ids.filter((letter) => letter <= "S");
    const mapped = {};
    preferred.forEach((id) => {
      const lower = id.toLowerCase();
      mapped[id] = nodes[lower] ?? null;
    });
    mapped.W = nodes.w ?? null;
    return mapped;
  }, [nodes]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl shadow-xl border overflow-hidden"
      style={{ borderColor: "#d4af37" }}
    >
      <div className="p-5 text-white" style={{ background: "var(--color-morado)" }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest opacity-80">Mi Pináculo</p>
            <h2 className="text-2xl font-bold">{profileName}</h2>
            <p className="text-sm opacity-90">Nacimiento: {formatDate(birthDate)}</p>
          </div>
          <div className="text-xs md:text-right opacity-90">
            <div>Última actualización</div>
            <div className="font-semibold">{formatDate(data.updatedAt || data.calculatedAt)}</div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-6 bg-white">
        <section className="space-y-5">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <PinnacleDiagram results={diagramData} />
            <PinnacleResultList results={diagramData} />
          </div>
          <div className="flex items-center justify-center">
            <a
              href="https://numerologia-cotidiana.com/pinaculo/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-morado text-white text-sm font-semibold shadow-lg hover:opacity-90 transition"
            >
              Saber Más del Pináculo
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>
      </div>
    </motion.div>
  );
}


